/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { cssMap, cx, jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { type TriggerProps } from '@atlaskit/popup';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import Suspense from '@atlaskit/react-ufo/suspense';
import { token } from '@atlaskit/tokens';

import { ShimmerText } from '../../compact-research-steps/ShimmerText';
import { HoverCard } from '../../hover-card/HoverCard';
import { getBorderColor, getIconColor } from '../skill-colors';
import { SkillHoverCardContent } from '../skill-hover-card-content/SkillHoverCardContent';
import { type SkillIconComponent, skillIconMap } from '../skill-icons';
import type { Skill } from '../types';

export type RovoSkillTagProps = Omit<Skill, 'id'> & {
	// invert colors for use with dark backgrounds
	invertColors?: boolean;
	// disable hovercard functionality
	disableHovercard?: boolean;
	// show shimmer effect
	shimmer?: boolean;
	// optional max-height applied to the hover card content container
	hovercardMaxHeight?: number | string;
};

const styles = cssMap({
	parallelogram: {
		marginLeft: token('space.050'),
		paddingLeft: token('space.075'),
		paddingRight: token('space.075'),
		borderRadius: token('radius.xsmall'),
		// to create a parallelogram, skew the span -15 degrees
		transform: 'skewX(-15deg)',
		display: 'inline-block',
	},
	background: {
		backgroundColor: token('color.background.neutral'),
	},
	backgroundInverse: {
		backgroundColor: token('color.background.inverse.subtle'),
	},
	parallelogramWithWrapping: {
		boxSizing: 'border-box',
		maxWidth: '100%',
	},
	borderAccent: {
		borderLeftStyle: 'solid',
		borderLeftWidth: token('border.width.selected'),
	},
	content: {
		// skew the contents of the span back to normal orientation so that only the outer element is skewed
		transform: 'skewX(15deg)',
	},
	contentWithWrapping: {
		display: 'block',
		maxWidth: '100%',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
	},
	labelWithWrapping: {
		display: 'inline',
		verticalAlign: 'bottom',
	},
	slashWithWrapping: {
		display: 'inline',
		marginRight: token('space.050'),
	},
	iconWithWrapping: {
		display: 'inline-flex',
		marginRight: token('space.050'),
		verticalAlign: 'text-bottom',
	},
});

const SkillTagInternal = ({
	triggerProps = {},
	invertColors,
	slug,
	IconComponent,
	onMouseEnter,
	onMouseLeave,
	color,
	shimmer,
}: {
	triggerProps?: Partial<TriggerProps>;
	onMouseEnter?: React.MouseEventHandler;
	onMouseLeave?: React.MouseEventHandler;
	IconComponent?: SkillIconComponent;
} & Omit<RovoSkillTagProps, 'iconName'>) => {
	const borderColor = getBorderColor(color, invertColors);
	const iconColor = getIconColor(color, invertColors);
	const shouldWrapLabel = fg('rovo_skill_tag_label_wrapping');

	return (
		<Box
			as="span"
			xcss={cx(
				styles.parallelogram,
				!shouldWrapLabel && styles.borderAccent,
				shouldWrapLabel && styles.parallelogramWithWrapping,
				invertColors ? styles.backgroundInverse : styles.background,
			)}
			/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Border color is derived at runtime from skill metadata. */
			style={{ borderLeftColor: shouldWrapLabel ? undefined : borderColor }}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			{...triggerProps}
		>
			{shouldWrapLabel ? (
				<Box as="span" xcss={cx(styles.content, styles.contentWithWrapping)}>
					<Box
						as="span"
						xcss={styles.slashWithWrapping}
						/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Slash color is derived at runtime from skill metadata. */
						style={{ color: iconColor }}
					>
						/
					</Box>
					{IconComponent && (
						<Box as="span" xcss={styles.iconWithWrapping}>
							<Suspense fallback={null} interactionName="skill-tag-icon">
								<IconComponent label="" size="small" color={iconColor} />
							</Suspense>
						</Box>
					)}
					<Box as="span" xcss={styles.labelWithWrapping}>
						{shimmer ? (
							<ShimmerText as="span">{slug}</ShimmerText>
						) : (
							<Text color={invertColors ? 'color.text.inverse' : 'color.text'}>{slug}</Text>
						)}
					</Box>
				</Box>
			) : (
				<Inline xcss={styles.content} space="space.050" alignBlock="center">
					{IconComponent && (
						<Suspense fallback={null} interactionName="skill-tag-icon">
							<IconComponent label="" size="small" color={iconColor} />
						</Suspense>
					)}
					{shimmer ? (
						<ShimmerText>{slug}</ShimmerText>
					) : (
						<Text color={invertColors ? 'color.text.inverse' : 'color.text'}>{slug}</Text>
					)}
				</Inline>
			)}
		</Box>
	);
};

// delay opening and closing of the hovercard to prevent flickering
const SKILL_TAG_HOVERCARD_DELAY = 200;

export const SkillTag = ({
	invertColors = false,
	disableHovercard = false,
	product,
	iconName,
	hovercardMaxHeight,
	...props
}: RovoSkillTagProps): JSX.Element => {
	const IconComponent = iconName ? skillIconMap[iconName] : undefined;
	const hasHovercardInfo = props?.name && props?.description;

	return disableHovercard || !hasHovercardInfo ? (
		<SkillTagInternal
			{...props}
			invertColors={invertColors}
			disableHovercard={disableHovercard}
			IconComponent={IconComponent}
		/>
	) : (
		<HoverCard
			placement="top"
			fallbackPlacements={['bottom', 'left']}
			autoFocus={false}
			hoverCardDelay={SKILL_TAG_HOVERCARD_DELAY}
			shouldRenderToParent={false}
			content={
				<SkillHoverCardContent
					product={product}
					iconName={iconName}
					maxHeight={hovercardMaxHeight}
					{...props}
				/>
			}
		>
			<SkillTagInternal
				invertColors={invertColors}
				disableHovercard={disableHovercard}
				IconComponent={IconComponent}
				{...props}
			/>
		</HoverCard>
	);
};
